import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { IoMdRefresh } from "react-icons/io";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useEffect, useMemo, useRef, useState } from "react";
import { IOrgSubService } from "@/api_framework/api_modals/Org";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import { ICBLstatsBodyprops } from "@/api_framework/api_modals/CBL";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { useCBLListAPI } from "@/app/hooks/api_hooks/PWL/useCBLListAPI";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import { useCBLStatsAPI } from "@/app/hooks/api_hooks/PWL/useCBLStatsAPI";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { useStaffClinicListAPI } from "@/app/hooks/api_hooks/clinic/useStaffClinicListAPI";
import { useOrgServicesByFilterAPI } from "@/app/hooks/api_hooks/org/useOrgServicesByFilterAPI";
import { useOverBookingBdeUserListAPI } from "@/app/hooks/api_hooks/bookings/useOverBookingBdeUserListAPI";
import { IPatientWaitingListState, setCBLFilters } from "@/app_redux/reducers/slices/auth/patient_waiting_list_slice";

export interface IPWLFilterFormFields {
  created_date: string;
  booking_date: string;
  city: Array<string>;
  clinic: Array<string>;
  service: Array<string>;
  subService: Array<string>;
  createdBy: Array<string>;
}

const CBLFiltersComponent = () => {
  const stringConstants = useStringConstants();
  const currentDate = format(new Date(Date.now()), stringConstants.fieldDOBFormat);
  const { getRouteKey } = useRouter();
  const { routeState } = useRouteInfo(getRouteKey("PATIENT_WAITING_LIST", "id"));
  const { clinics, cities, CBLFilters } = routeState.state as IPatientWaitingListState;
  const { serviceTypes, execute: fetchOrgServicesByFilter } = useOrgServicesByFilterAPI();
  const didFetchAdvancePaymentServices = useRef(false);
  const { routeState: routeId } = useRouteInfo(getRouteKey("SLOTS", "id"));
  const { bdeUserList } = routeId.state as ISlotsState;
  const fetchPWLClinics = useStaffClinicListAPI("PATIENT_WAITING_LIST");
  const fetchOrgCities = useOrgCitiesListAPI("PATIENT_WAITING_LIST");
  const fetchUserList = useOverBookingBdeUserListAPI();
  const fetcCBLStats = useCBLStatsAPI();
  const fetcCBLList = useCBLListAPI();
  const isFiltersLoading = useLoader("isOrgCitiesInProgress");
  const dispatch = useDispatch();
  const [firstRender, setFirstRender] = useState<boolean>(false);
  const didHydrateSavedFilters = useRef(false);
  const orgCityItems = useSelectMenuReducer(cities, "name", "id");
  const orgClinicItems = useSelectMenuReducer(clinics, "display_name", "id");
  const bdeUserListItem = useSelectMenuReducer(bdeUserList, "name", "user_id", {
    withAllSelectOption: true,
    allSelectItemOptions: {
      title: "App Booking",
    },
  });

  const serviceTypeItems = useMemo(
    () =>
      (serviceTypes ?? []).map((serviceType) => ({
        id: serviceType?.id,
        name: serviceType?.name,
      })),
    [serviceTypes],
  );

  const serviceTypeToServiceIds = useMemo(() => {
    return (serviceTypes ?? []).reduce(
      (final, serviceType) => {
        final[serviceType.id] = (serviceType?.services ?? []).map((service) => service.service_id);
        return final;
      },
      {} as Record<string, string[]>,
    );
  }, [serviceTypes]);

  const serviceIdToServiceTypeId = useMemo(() => {
    return (serviceTypes ?? []).reduce(
      (final, serviceType) => {
        (serviceType?.services ?? []).forEach((service) => {
          final[service.service_id] = serviceType.id;
        });
        return final;
      },
      {} as Record<string, string>,
    );
  }, [serviceTypes]);

  const subServiceIdToServiceId = useMemo(() => {
    return (serviceTypes ?? []).reduce(
      (final, serviceType) => {
        (serviceType?.services ?? []).forEach((service) => {
          (service?.sub_services ?? []).forEach((subService) => {
            final[subService.sub_service_id] = service.service_id;
          });
        });
        return final;
      },
      {} as Record<string, string>,
    );
  }, [serviceTypes]);

  const getServiceIdsFromSelectedFilters = useMemo(
    () =>
      (selectedServiceTypeIds: string[] = [], selectedSubServiceIds: string[] = []) => {
        const serviceIdsByType = Array.from(
          new Set(selectedServiceTypeIds.flatMap((serviceTypeId) => serviceTypeToServiceIds[serviceTypeId] ?? [])),
        );
        const serviceIdsBySubService = Array.from(
          new Set(selectedSubServiceIds.map((subServiceId) => subServiceIdToServiceId[subServiceId]).filter(Boolean)),
        );
        if (serviceIdsBySubService.length === 0) return serviceIdsByType;
        return serviceIdsBySubService.filter((serviceId) => serviceIdsByType.includes(serviceId));
      },
    [serviceTypeToServiceIds, subServiceIdToServiceId],
  );

  const formHook = useForm<IPWLFilterFormFields>({
    mode: "onChange",
    defaultValues: {
      created_date: null,
      booking_date: currentDate,
      createdBy: ["1"],
    },
  });

  const l1ServiceItems = useSelectMenuReducer(serviceTypeItems, "name", "id");
  const serviceWatch = formHook.watch("service");

  const filteredSubServices = useMemo(() => {
    if (!serviceWatch || serviceWatch.length === 0) return [];
    const allSubServices: IOrgSubService[] = [];
    (serviceTypes ?? []).forEach((serviceType) => {
      if (!serviceWatch.includes(serviceType.id)) return;
      (serviceType?.services ?? []).forEach((service) => {
        (service?.sub_services ?? []).forEach((subService) => {
          allSubServices.push(subService);
        });
      });
    });
    const uniqueSubServices = Array.from(
      new Map(allSubServices.map((subService) => [subService.sub_service_id, subService])).values(),
    );
    return uniqueSubServices;
  }, [serviceTypes, serviceWatch]);

  const l2ServiceItems: ISelectMenuItemData[] = useSelectMenuReducer(
    filteredSubServices,
    "sub_service_name",
    "sub_service_id",
  );

  useEffect(() => {
    const formFields = formHook.getValues();
    fetchPWLClinics({
      cityIds: formFields.city,
      withProviders: true,
    });
  }, [formHook.watch("city")]);

  useEffect(() => {
    fetchUserList();
    fetchOrgCities({
      withClinics: false,
      withClinicProviders: false,
      clinicOptions: {
        selectFirst: false,
      },
    });
  }, []);

  useEffect(() => {
    if (didFetchAdvancePaymentServices.current) return;
    didFetchAdvancePaymentServices.current = true;
    fetchOrgServicesByFilter({
      filterType: "advance_payment_booking",
    });
  }, [fetchOrgServicesByFilter]);

  const onSubmit = async (data: IPWLFilterFormFields) => {
    if (data) {
      const selectedServiceIds = getServiceIdsFromSelectedFilters(data.service, data.subService);
      const constructedData: ICBLstatsBodyprops = {
        booking_date: data.booking_date,
        created_date: data.created_date,
        clinic_details: {
          id: data.clinic,
        },
        service_details: {
          id: selectedServiceIds,
        },
        sub_service_details: {
          id: data.subService,
        },
        created_by: {
          id: bdeUserList?.length < data.createdBy?.length ? ["1"] : data.createdBy,
        },
      };
      const constructedFilters: ICBLstatsBodyprops = {
        booking_date: data.booking_date,
        created_date: data.created_date,
        city_details: {
          id: data.city,
        },
        clinic_details: {
          id: data.clinic,
        },
        service_details: {
          id: selectedServiceIds,
        },
        sub_service_details: {
          id: data.subService,
        },
        created_by: {
          id: bdeUserList?.length < data.createdBy?.length ? ["1"] : data.createdBy,
        },
      };
      fetcCBLStats(constructedData);
      fetcCBLList(constructedData, "on_hold");
      dispatch(setCBLFilters({ data: constructedFilters }));
    }
  };

  useEffect(() => {
    const formdata = formHook.getValues();
    if (
      formdata.clinic?.length > 0 &&
      formdata.service?.length > 0 &&
      formdata?.subService?.length > 0 &&
      formdata?.createdBy?.length > 0 &&
      !firstRender
    ) {
      setFirstRender(true);
      const selectedServiceIds = getServiceIdsFromSelectedFilters(formdata.service, formdata.subService);
      const constructedData: ICBLstatsBodyprops = {
        booking_date: formdata.booking_date,
        created_date: formdata.created_date,
        clinic_details: {
          id: formdata.clinic,
        },
        service_details: {
          id: selectedServiceIds,
        },
        sub_service_details: {
          id: formdata.subService,
        },
        created_by: {
          id: bdeUserList?.length < formdata.createdBy?.length ? ["1"] : formdata.createdBy,
        },
      };
      const constructedFilters: ICBLstatsBodyprops = {
        booking_date: formdata.booking_date,
        created_date: formdata.created_date,
        city_details: {
          id: formdata.city,
        },
        clinic_details: {
          id: formdata.clinic,
        },
        service_details: {
          id: selectedServiceIds,
        },
        sub_service_details: {
          id: formdata.subService,
        },
        created_by: {
          id: bdeUserList?.length < formdata.createdBy?.length ? ["1"] : formdata.createdBy,
        },
      };
      fetcCBLStats(constructedData);
      fetcCBLList(constructedData, "on_hold");
      dispatch(setCBLFilters({ data: constructedFilters }));
    }
  }, [formHook.watch("clinic"), formHook.watch("service"), formHook.watch("subService"), formHook.watch("createdBy")]);

  useEffect(() => {
    if (!CBLFilters || didHydrateSavedFilters.current) return;
    const savedServiceIds = CBLFilters?.service_details?.id ?? [];
    const mappedServiceTypeIds = Array.from(
      new Set(savedServiceIds.map((serviceId) => serviceIdToServiceTypeId[serviceId]).filter(Boolean)),
    );
    if (
      savedServiceIds.length > 0 &&
      mappedServiceTypeIds.length === 0 &&
      Object.keys(serviceIdToServiceTypeId).length === 0
    ) {
      return;
    }
    const credited = CBLFilters?.created_by?.id[0] === "1";
    if (credited) {
      formHook.setValue(
        "createdBy",
        bdeUserListItem.map((id) => id?.id),
      );
    } else {
      formHook.setValue("createdBy", CBLFilters?.created_by?.id);
    }
    formHook.setValue("city", CBLFilters?.city_details?.id);
    formHook.setValue("clinic", CBLFilters?.clinic_details?.id);
    formHook.setValue("service", mappedServiceTypeIds);
    formHook.setValue("subService", CBLFilters?.sub_service_details?.id);
    formHook.setValue("booking_date", CBLFilters?.booking_date);
    formHook.setValue("created_date", CBLFilters?.created_date);
    didHydrateSavedFilters.current = true;
  }, [CBLFilters, bdeUserListItem, serviceIdToServiceTypeId]);

  if (isFiltersLoading) {
    return (
      <div className="mt-2">
        <TableSkeletonComponent
          columnLength={8}
          rowLength={2}
          bodyLength={8}
          containerClassName="mt-0 h-[150px] w-full overflow-hidden rounded-lg border border-[#ECECEC]"
        />
      </div>
    );
  }

  return (
    <div className="mt-2">
      <form
        onSubmit={formHook.handleSubmit(onSubmit)}
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-8 gap-2 sm:gap-3 py-1 items-end"
      >
        <div className="w-full min-w-0 flex flex-col gap-0.5">
          <div className="text-xs font-normal leading-none flex items-center cursor-pointer gap-1">
            Creation Date
            <span className="cursor-pointer">
              <IoMdRefresh onClick={() => formHook.setValue("created_date", null)} />
            </span>
          </div>
          <PWLCalendarComponent
            showFutureYears
            allowPastDaySelection={true}
            errorMessages={[{ message: "Date is required", type: "required" }]}
            register={formHook.register("created_date", {
              required: formHook.watch("booking_date") ? false : true,
            })}
            showTypedErrors={false}
            showLeftNav={false}
            fieldError={formHook.formState.errors.created_date}
            onSelectDate={(date) => {
              formHook.setValue("created_date", format(date, "yyyy-MM-dd"));
            }}
            defaultSelectedDate={formHook.watch("created_date")}
            className="w-full h-[34px] py-1 px-2.5 pr-9"
          />
        </div>
        <div className="w-full min-w-0 flex flex-col gap-0.5">
          <div className="text-xs font-normal leading-none flex items-center gap-1">
            Bookings Date
            <span className="cursor-pointer">
              <IoMdRefresh onClick={() => formHook.setValue("booking_date", null)} />
            </span>
          </div>
          <PWLCalendarComponent
            showFutureYears
            allowPastDaySelection={true}
            errorMessages={[{ message: "Date is required", type: "required" }]}
            register={formHook.register("booking_date", {
              required: formHook.watch("created_date") ? false : true,
            })}
            showTypedErrors={false}
            showLeftNav={false}
            fieldError={formHook.formState.errors.booking_date}
            onSelectDate={(date) => {
              formHook.setValue("booking_date", format(date, "yyyy-MM-dd"));
            }}
            defaultSelectedDate={formHook.watch("booking_date")}
            className="w-full h-[34px] py-1 px-2.5 pr-9"
          />
        </div>
        <div className="w-full min-w-0">
          <SearchableMultiSelectMenu
            onSelectItem={(items) => {
              const cityId = items.map((i) => i.id);
              formHook.setValue("city", cityId);
            }}
            label="City"
            defaultSelectAll
            errorMessages={[{ message: "Required", type: "required" }]}
            fieldError={formHook.formState.errors.city}
            register={formHook.register("city", {
              required: true,
            })}
            withSelectAll
            showTooltips={true}
            showTypedErrors={false}
            containerClassName="w-full"
            placeholder="Select City"
            defaultSelected={orgCityItems.filter((c) => formHook.watch("city")?.includes(c.id))}
            selectItems={orgCityItems}
          />
        </div>
        <div className="w-full min-w-0">
          <SearchableMultiSelectMenu
            onSelectItem={(items) => {
              const clinicId = items.map((i) => i.id);
              formHook.setValue("clinic", clinicId);
            }}
            label="Clinic"
            defaultSelectAll
            errorMessages={[{ message: "Required", type: "required" }]}
            fieldError={formHook.formState.errors.clinic}
            register={formHook.register("clinic", {
              required: true,
            })}
            withSelectAll
            showTooltips={true}
            showTypedErrors={false}
            containerClassName="w-full"
            placeholder="Select Clinic"
            defaultSelected={orgClinicItems.filter((c) => formHook.watch("clinic")?.includes(c.id))}
            selectItems={orgClinicItems}
          />
        </div>
        <div className="w-full min-w-0">
          <SearchableMultiSelectMenu
            onSelectItem={(items) => {
              const serviceTypeIds = items.map((i) => i.id);
              formHook.setValue("service", serviceTypeIds);
              formHook.setValue("subService", []);
            }}
            defaultSelectAll
            label="Service Type"
            errorMessages={[{ message: "Required", type: "required" }]}
            fieldError={formHook.formState.errors.service}
            register={formHook.register("service", {
              required: true,
            })}
            withSelectAll
            showTooltips={true}
            showTypedErrors={false}
            containerClassName="w-full"
            placeholder="Select Service Type"
            defaultSelected={l1ServiceItems.filter((c) => formHook.watch("service")?.includes(c.id))}
            selectItems={l1ServiceItems}
          />
        </div>
        <div className="w-full min-w-0">
          <SearchableMultiSelectMenu
            onSelectItem={(items) => {
              const subsServiceId = items.map((i) => i.id);
              formHook.setValue("subService", subsServiceId);
            }}
            defaultSelectAll
            label="Sub Service"
            errorMessages={[{ message: "Required", type: "required" }]}
            fieldError={formHook.formState.errors.subService}
            register={formHook.register("subService", {
              required: true,
            })}
            withSelectAll
            showTooltips={true}
            showTypedErrors={false}
            containerClassName="w-full"
            placeholder="Select Sub Service"
            defaultSelected={l2ServiceItems.filter((c) => formHook.watch("subService")?.includes(c.id))}
            selectItems={l2ServiceItems}
          />
        </div>
        <div className="w-full min-w-0">
          <SearchableMultiSelectMenu
            onSelectItem={(items) => {
              const createdById = items.map((i) => i.id);
              formHook.setValue("createdBy", createdById);
            }}
            label="Created By"
            errorMessages={[{ message: "Required", type: "required" }]}
            fieldError={formHook.formState.errors.createdBy}
            register={formHook.register("createdBy", {
              required: true,
            })}
            withSelectAll
            showTooltips={true}
            showTypedErrors={false}
            containerClassName="w-full"
            placeholder="Select User"
            defaultSelected={bdeUserListItem.filter((c) => formHook.watch("createdBy")?.includes(c.id))}
            selectItems={bdeUserListItem}
          />
        </div>
        <div className="w-full min-w-0 flex items-end sm:col-span-2 md:col-span-3 xl:col-span-1">
          <SolidButton
            size="LG"
            asType="submit"
            asSubmitText={"Apply Filters"}
            className="w-full sm:w-auto py-2.5 !bg-black hover:!bg-black focus:!bg-black !text-white text-xs font-medium rounded-md px-5 border-transparent cursor-pointer"
          />
        </div>
      </form>
      {!formHook.watch("booking_date") && !formHook.watch("created_date") && (
        <span className="text-red-400 text-[10px] sm:text-xs mt-1 block">Please fill one of these dates</span>
      )}
    </div>
  );
};

export default CBLFiltersComponent;
